<template>
  <div
    class="carousel"
    ref="carouselRef"
    @mouseenter="onHoverStart"
    @mouseleave="onHoverEnd"
    @touchstart="onTouchStart"
    @touchend="onTouchEnd"
  >
    <div class="carousel__container" @scroll="handleScroll">
      <slot></slot>
    </div>

    <div class="carousel__dots" v-if="showDots && slideCount > 1">
      <button
        v-for="index in slideCount"
        :key="index - 1"
        class="carousel__dot"
        :class="{ 'carousel__dot--active': selectedIndex === index - 1 }"
        @click="scrollTo(index - 1)"
      ></button>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount, watch } from 'vue'

const props = defineProps({
  showDots: Boolean,
  slideCount: {
    type: Number,
    default: 0
  },
  autoplay: {
    type: Object,
    default: null
  }
});

// autoplay: { enabled, interval, hover, touch, resume, delay }

const carouselRef = ref(null)
const selectedIndex = ref(0)
const intervalId = ref(null)
const scrollTimeout = ref(null)
const resumeTimeout = ref(null)
const paused = ref(false)

const scrollTo = (index) => {
  if (!carouselRef.value) return
  const container = carouselRef.value.querySelector('.carousel__container')
  const slides = container.querySelectorAll('.carousel__slide')
  if (slides[index]) {
    container.scrollTo({ left: slides[index].offsetLeft, behavior: 'smooth' })
    selectedIndex.value = index
  }
}

const handleScroll = () => {
  if (scrollTimeout.value) clearTimeout(scrollTimeout.value)
  scrollTimeout.value = setTimeout(updateSelectedIndex, 50)
}

const updateSelectedIndex = () => {
  if (!carouselRef.value) return
  const container = carouselRef.value.querySelector('.carousel__container')
  const slides = container.querySelectorAll('.carousel__slide')
  if (!slides.length) return
  const index = Math.round(container.scrollLeft / slides[0].offsetWidth)
  selectedIndex.value = Math.max(0, Math.min(index, slides.length - 1))
}

const clearTimers = () => {
  if (intervalId.value) { clearInterval(intervalId.value); intervalId.value = null }
  if (resumeTimeout.value) { clearTimeout(resumeTimeout.value); resumeTimeout.value = null }
}

const startAutoplay = () => {
  clearTimers()
  const a = props.autoplay
  if (!a?.enabled || paused.value) return
  intervalId.value = setInterval(() => {
    scrollTo((selectedIndex.value + 1) % props.slideCount)
  }, a.interval ?? 2000)
}

const pause = () => {
  paused.value = true
  clearTimers()
}

const resume = () => {
  const a = props.autoplay
  if (!a?.enabled || a.resume === false) return
  clearTimers()
  const delay = a.delay ?? 0
  if (delay > 0) {
    resumeTimeout.value = setTimeout(() => {
      paused.value = false
      startAutoplay()
    }, delay)
  } else {
    paused.value = false
    startAutoplay()
  }
}

const onHoverStart = () => {
  if (props.autoplay?.hover !== false) pause()
}

const onHoverEnd = () => {
  if (props.autoplay?.hover !== false) resume()
}

const onTouchStart = () => {
  if (props.autoplay?.touch !== false) pause()
}

const onTouchEnd = () => {
  if (props.autoplay?.touch !== false) resume()
}

watch(() => props.autoplay, () => {
  if (props.autoplay?.enabled) {
    paused.value = false
    startAutoplay()
  } else {
    pause()
  }
}, { deep: true })

onMounted(() => {
  if (!carouselRef.value) return
  const container = carouselRef.value.querySelector('.carousel__container')
  const slides = container.querySelectorAll('.carousel__slide')

  const observer = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        const idx = Array.from(slides).indexOf(entry.target)
        if (idx !== -1) selectedIndex.value = idx
      }
    })
  }, { root: container, threshold: 0.7 })

  slides.forEach(slide => observer.observe(slide))
  carouselRef.value._observer = observer

  container.addEventListener('focusin', pause)
  container.addEventListener('focusout', resume)

  if (props.autoplay?.enabled) startAutoplay()
})

onBeforeUnmount(() => {
  if (carouselRef.value?._observer) carouselRef.value._observer.disconnect()
  clearTimers()
  if (scrollTimeout.value) clearTimeout(scrollTimeout.value)
})
</script>

<style>
.carousel {
  position: relative;
  width: 100%;
  overflow: hidden;
}

.carousel__container {
  display: flex;
  scroll-snap-type: x mandatory;
  overflow-x: scroll;
  scrollbar-width: none;
  -ms-overflow-style: none;
  scroll-behavior: smooth;
}

.carousel__container::-webkit-scrollbar {
  display: none;
}

.carousel__slide {
  flex: 0 0 100%;
  width: 100%;
  scroll-snap-align: start;
  scroll-snap-stop: always;
}

.carousel__dots {
  display: flex;
  justify-content: center;
  gap: 0.5rem;
  margin-top: 1rem;
}

.carousel__dot {
  width: 0.625rem;
  height: 0.625rem;
  border-radius: 50%;
  background-color: rgba(0, 0, 0, 0.2);
  border: none;
  padding: 0;
  cursor: pointer;
  transition: background-color 0.3s ease;
}

.carousel__dot--active {
  background-color: rgba(0, 0, 0, 0.6);
}
</style>
